/**
 * Cost rates per API integration unit.
 *
 * These are the COGS Orbit pays providers, in ZAR cents per unit. Update
 * whenever a provider repricing lands. The platform-admin UI uses these to
 * answer "what is this customer actually costing us this month?"
 *
 * Source rates (approximate, May 2026):
 *   - OpenAI GPT-4o-mini: ~USD 0.15 / 1M input tokens, USD 0.60 / 1M output
 *     (we use a blended USD 0.45 / 1M total here)
 *   - Twilio voice (ZA inbound): ~USD 0.0175 / minute
 *   - Twilio recording storage: ~USD 0.0005 / min / month
 *   - Twilio WhatsApp user-initiated: ~USD 0.005 / message
 *   - SendGrid email: ~USD 0.00012 / message
 *   - AWS S3 storage: negligible at this volume
 *
 * USD -> ZAR using a fixed conservative rate of 19 ZAR/USD. The real backend
 * should read live FX from a daily snapshot in config-service.
 */

export type IntegrationKey =
  | "openai"
  | "twilio_voice"
  | "twilio_recording"
  | "twilio_whatsapp"
  | "sendgrid";

export interface CostRate {
  key: IntegrationKey;
  label: string;
  /** What the unit measures, e.g. "tokens" or "minutes" or "messages". */
  unit: string;
  /** ZAR cents per unit. Fractions allowed — these get summed into integers. */
  zarCentsPerUnit: number;
  /** Brand accent hex used in charts/legend chips. */
  color: string;
}

const USD_ZAR = 19;

export const COST_RATES: Record<IntegrationKey, CostRate> = {
  openai: {
    key: "openai",
    label: "OpenAI",
    unit: "token",
    // 0.45 USD / 1M tokens -> ZAR cents / token
    zarCentsPerUnit: (0.45 * USD_ZAR * 100) / 1_000_000,
    color: "#7c5cff"
  },
  twilio_voice: {
    key: "twilio_voice",
    label: "Twilio Voice",
    unit: "minute",
    // 0.0175 USD / min -> ZAR cents / minute
    zarCentsPerUnit: 0.0175 * USD_ZAR * 100,
    color: "#00d4ff"
  },
  twilio_recording: {
    key: "twilio_recording",
    label: "Recording storage",
    unit: "minute-month",
    zarCentsPerUnit: 0.0005 * USD_ZAR * 100,
    color: "#2ee69b"
  },
  twilio_whatsapp: {
    key: "twilio_whatsapp",
    label: "Twilio WhatsApp",
    unit: "message",
    zarCentsPerUnit: 0.005 * USD_ZAR * 100,
    color: "#ff5ca7"
  },
  sendgrid: {
    key: "sendgrid",
    label: "SendGrid",
    unit: "email",
    zarCentsPerUnit: 0.00012 * USD_ZAR * 100,
    color: "#ffb547"
  }
};

export const INTEGRATION_KEYS: IntegrationKey[] = [
  "openai", "twilio_voice", "twilio_recording", "twilio_whatsapp", "sendgrid"
];

export function costCents(key: IntegrationKey, units: number): number {
  return Math.round(units * COST_RATES[key].zarCentsPerUnit);
}
